import React, { useState } from 'react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';

const AddVisitor = () => {
	const { isDark } = useTheme();
	const [form, setForm] = useState({
		fullName: '',
		phone: '',
		email: '',
		howHeard: '',
		ageRange: '',
		occupation: '',
		location: '',
		firstTime: 'Yes',
		prayerRequests: '',
		feedback: '',
		firstVisitDate: new Date().toISOString().slice(0, 10),
		receivedFollowUp: '',
		baptized: '',
		workforce: '',
		previousParish: '',
	});
	const [visitType, setVisitType] = useState('first');

	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');

	// API Base URL - should be configured in environment
	const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

	const handleSubmit = async e => {
		e.preventDefault();
		
		try {
			setLoading(true);
			
			// Prepare data for backend
			const visitorData = {
				first_name: form.fullName.split(' ')[0] || form.fullName,
				last_name: form.fullName.split(' ').slice(1).join(' ') || '',
				phone: form.phone,
				email: form.email || null,
				visitor_type: form.firstTime === 'Yes' ? 'first_timer' : 'returning_member',
				address: form.location || null,
				age: form.ageRange ? parseInt(form.ageRange) : null,
				gender: form.occupation || null,
				visit_date: form.firstVisitDate,
				service_type: 'sunday',
				notes: form.prayerRequests || form.feedback || null,
				source: form.howHeard || null
			};

			const response = await fetch(`${API_BASE_URL}/visitors`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(visitorData)
			});
			
			const data = await response.json();
			
			if (data.success) {
				setMessage('Visitor added successfully!');
				// Reset form
				setForm({
					fullName: '',
					phone: '',
					email: '',
					howHeard: '',
					ageRange: '',
					occupation: '',
					location: '',
					firstTime: 'Yes',
					prayerRequests: '',
					feedback: '',
					firstVisitDate: new Date().toISOString().slice(0, 10),
					receivedFollowUp: '',
					baptized: '',
					workforce: '',
					previousParish: '',
				});
			} else {
				setMessage(data.error || 'Failed to add visitor');
			}
		} catch (error) {
			console.error('Error adding visitor:', error);
			setMessage('Error adding visitor. Please check your connection and try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Layout title="Add New Visitor">
			<div className={`max-w-3xl mx-auto rounded-xl shadow p-6 ${isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white text-indigo-900'}`}>
				<div className="flex gap-3 mb-4">
					<button
						type="button"
						onClick={() => setVisitType('first')}
						className={`${visitType === 'first' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700'} px-4 py-2 rounded-xl font-semibold`}
					>
						🆕 I’m a First Timer
					</button>
					<button
						type="button"
						onClick={() => setVisitType('second')}
						className={`${visitType === 'second' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700'} px-4 py-2 rounded-xl font-semibold`}
					>
						🔁 I’ve Been Here Before
					</button>
				</div>

				{/* Message Display */}
				{message && (
					<div className={`mt-4 p-4 rounded-lg ${
						message.includes('successfully') 
							? 'bg-green-100 text-green-800'
							: 'bg-red-100 text-red-800'
					}`}>
						{message}
						<button 
							onClick={() => setMessage('')}
							className="float-right font-bold"
						>
							×
						</button>
					</div>
				)}

				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm mb-1">Full Name</label>
							<input className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.fullName} onChange={e=>setForm({...form, fullName:e.target.value})} required />
						</div>
						<div>
							<label className="block text-sm mb-1">Phone Number</label>
							<input className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} required />
						</div>
						<div>
							<label className="block text-sm mb-1">Email Address</label>
							<input type="email" className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.email} onChange={e=>setForm({...form, email:e.target.value})} />
						</div>
						<div>
							<label className="block text-sm mb-1">How did you hear about us?</label>
							<select className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.howHeard} onChange={e=>setForm({...form, howHeard:e.target.value})}>
								<option value="">Select</option>
								<option>Friend/Family</option>
								<option>Social Media</option>
								<option>Outreach</option>
								<option>Online Search</option>
								<option>Other</option>
							</select>
						</div>
						<div>
							<label className="block text-sm mb-1">Age Range</label>
							<select className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.ageRange} onChange={e=>setForm({...form, ageRange:e.target.value})}>
								<option>Under 18</option>
								<option>18 - 25</option>
								<option>26 - 35</option>
								<option>36 - 60</option>
								<option>60+</option>
							</select>
						</div>
						<div>
							<label className="block text-sm mb-1">Occupation</label>
							<input className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.occupation} onChange={e=>setForm({...form, occupation:e.target.value})} />
						</div>
						<div>
							<label className="block text-sm mb-1">Location</label>
							<input className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.location} onChange={e=>setForm({...form, location:e.target.value})} />
						</div>
						<div>
							<label className="block text-sm mb-1">First-time visitor?</label>
							<select className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.firstTime} onChange={e=>setForm({...form, firstTime:e.target.value})}>
								<option>Yes</option>
								<option>No</option>
							</select>
						</div>
					</div>

					{visitType === 'first' ? (
						<div className="grid grid-cols-1 gap-4">
							<div>
								<label className="block text-sm mb-1">Prayer requests</label>
								<textarea rows={3} className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.prayerRequests} onChange={e=>setForm({...form, prayerRequests:e.target.value})} />
							</div>
							<div>
								<label className="block text-sm mb-1">Feedback</label>
								<textarea rows={3} className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.feedback} onChange={e=>setForm({...form, feedback:e.target.value})} />
							</div>
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm mb-1">When was your first time?</label>
								<input type="date" className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.firstVisitDate} onChange={e=>setForm({...form, firstVisitDate:e.target.value})} />
							</div>
							<div>
								<label className="block text-sm mb-1">Did you receive a call, text or visit?</label>
								<select className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.receivedFollowUp} onChange={e=>setForm({...form, receivedFollowUp:e.target.value})}>
									<option>Call</option>
									<option>Text</option>
									<option>Visit</option>
									<option>No</option>
								</select>
							</div>
							<div>
								<label className="block text-sm mb-1">Have you been baptized?</label>
								<select className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.baptized} onChange={e=>setForm({...form, baptized:e.target.value})}>
									<option>Yes</option>
									<option>No</option>
								</select>
							</div>
							<div>
								<label className="block text-sm mb-1">Join the workforce</label>
								<select className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.workforce} onChange={e=>setForm({...form, workforce:e.target.value})}>
									<option>Ministers</option>
									<option>Choir</option>
									<option>Ushering</option>
									<option>Technical</option>
									<option>Hospitality</option>
									<option>Security and Protocol</option>
									<option>Sanitary</option>
									<option>Children</option>
									<option>Elders</option>
								</select>
							</div>
							<div className="md:col-span-2">
								<label className="block text-sm mb-1">If they are already a member of RCCG before coming, which parish?</label>
								<input className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`} value={form.previousParish} onChange={e=>setForm({...form, previousParish:e.target.value})} />
							</div>
						</div>
					)}

					<button 
						type="submit" 
						disabled={loading}
						className="w-full py-3 rounded-xl font-semibold bg-indigo-600 text-white disabled:opacity-50"
					>
						{loading ? 'Saving...' : 'Save Visitor'}
					</button>
				</form>
			</div>
		</Layout>
	);
};

export default AddVisitor;
